import { supabase } from "@/integrations/supabase/client";
import type { ServiceType } from "@/lib/services";

export const ENQUIRY_STATUSES = ["new", "read", "replied", "closed"] as const;
export type EnquiryStatus = (typeof ENQUIRY_STATUSES)[number];

export const ENQUIRY_STATUS_LABEL: Record<EnquiryStatus, string> = {
  new: "New",
  read: "Read",
  replied: "Replied",
  closed: "Closed",
};

export const ENQUIRY_STATUS_CLASS: Record<EnquiryStatus, string> = {
  new: "bg-brand-soft text-brown-dark",
  read: "bg-secondary text-brown-dark",
  replied: "bg-emerald-100 text-emerald-800",
  closed: "bg-muted text-muted-foreground",
};

export interface Enquiry {
  id: string;
  service_type: ServiceType;
  listing_id: string | null;
  listing_kind: string;
  listing_title: string | null;
  customer_id: string | null;
  customer_name: string | null;
  customer_contact: string | null;
  partner_id: string;
  message: string | null;
  enquiry_status: EnquiryStatus;
  partner_reply: string | null;
  read_at: string | null;
  replied_at: string | null;
  closed_at: string | null;
  created_at: string;
}

export function normalizeStatus(value: string | null): EnquiryStatus {
  return (ENQUIRY_STATUSES as readonly string[]).includes(value ?? "")
    ? (value as EnquiryStatus)
    : "new";
}

export interface NewEnquiryInput {
  serviceType: ServiceType;
  listingId: string | null;
  listingKind: string;
  listingTitle: string | null;
  partnerId: string;
  customerId: string;
  name: string;
  mobile: string;
  message: string;
}

/** Customers can only create enquiries — never update or delete them. */
export async function createEnquiry(input: NewEnquiryInput) {
  const { error } = await supabase.from("partner_enquiries").insert({
    service_type: input.serviceType,
    listing_id: input.listingId,
    listing_kind: input.listingKind,
    listing_title: input.listingTitle,
    partner_id: input.partnerId,
    customer_id: input.customerId,
    customer_name: input.name.trim(),
    customer_contact: input.mobile.trim(),
    message: input.message.trim(),
    enquiry_status: "new",
  });
  if (error) throw error;
  const { recordAudit } = await import("@/lib/backend");
  await recordAudit({
    action: "enquiry_sent",
    entityType: "enquiry",
    entityId: input.listingId,
    serviceType: input.serviceType,
    metadata: { listingTitle: input.listingTitle, partnerId: input.partnerId },
  });
}

export async function listPartnerEnquiries(partnerId: string): Promise<Enquiry[]> {
  const { data, error } = await supabase
    .from("partner_enquiries")
    .select("*")
    .eq("partner_id", partnerId)
    .order("created_at", { ascending: false });
  if (error) throw error;
  return ((data as unknown as Enquiry[]) ?? []).map((r) => ({
    ...r,
    enquiry_status: normalizeStatus(r.enquiry_status),
  }));
}

export async function listCustomerEnquiries(customerId: string): Promise<Enquiry[]> {
  const { data, error } = await supabase
    .from("partner_enquiries")
    .select("*")
    .eq("customer_id", customerId)
    .order("created_at", { ascending: false });
  if (error) throw error;
  return ((data as unknown as Enquiry[]) ?? []).map((r) => ({
    ...r,
    enquiry_status: normalizeStatus(r.enquiry_status),
  }));
}

export async function markEnquiryRead(id: string) {
  const { error } = await supabase
    .from("partner_enquiries")
    .update({ enquiry_status: "read", read_at: new Date().toISOString() })
    .eq("id", id)
    .eq("enquiry_status", "new");
  if (error) throw error;
}

export async function replyToEnquiry(id: string, reply: string) {
  const { error } = await supabase
    .from("partner_enquiries")
    .update({
      partner_reply: reply.trim(),
      enquiry_status: "replied",
      replied_at: new Date().toISOString(),
    })
    .eq("id", id);
  if (error) throw error;
}

export async function closeEnquiry(id: string) {
  const { error } = await supabase
    .from("partner_enquiries")
    .update({ enquiry_status: "closed", closed_at: new Date().toISOString() })
    .eq("id", id);
  if (error) throw error;
}
